import * as _ from 'lodash';
import { DispatcherManager } from '../Dispatcher';
import { error } from '../../utility/Util';

/**
 * Creates proxy for preventing adding data to Dispatcher.appOperationData if data is not allowed to be within
 * dispatcher. Prevents adding dom elements, module components, components.
 *
 * Note: The only exception to the rules is when passing children configurations to warnings, dialogs and widgets,
 * "render" key can have module or component as its value.
 */
class DataProxy {
  /**
   * Enables proxying data by default in development mode.
   */
  public ENABLE_DATA_PROXY: boolean = process.env.NODE_ENV === 'development';

  /**
   * Dispatcher instance
   * @private {DispatcherManager} dispatcher
   */
  private static dispatcher: DispatcherManager;

  /**
   * AppOperationData proxy validator.
   */
  private validator = {
    get: Reflect.get, // default getter
    set: (obj, prop, value) => {
      let info = { path: [], message: '' };
      this.validateForbiddenWords(prop);
      if (!DataProxy.validateData(value, info)) {
        throw new Error(`Object "${prop}" has invalid property at path: "${info.path.reverse().join('.')}". ${info.message}`);
      }
      DataProxy.dispatcher.updateStateVariables(prop, value);
      return Reflect.set(obj, prop, value);
    }
  };

  private updateStateVariables = {
    get: Reflect.get,
    set: function (obj, prop, value) {
      DataProxy.dispatcher.updateStateVariables(prop, value);
      return Reflect.set(obj, prop, value);
    }
  };

  /**
   * Validates data before adding it to appOperationData.
   * @param value
   * @param obj
   */
  private static validateData = (
    value: any,
    obj: { path: string[]; message: string } = {
      path: [],
      message: ''
    }
  ): boolean => {
    let safe = true;
    if (_.isObject(value)) {
      let isReactElement = _.get(value, '$$typeof');
      if (_.isSymbol(isReactElement) && isReactElement.toString() === 'Symbol(react.element)') {
        safe = false;
        obj.message = 'React dom element can\'t be passed as dispatcher data.';
      }
      safe =
        safe &&
        Object.entries(value).every(function ([key, val]) {
          safe = safe && DataProxy.validateData(val, obj);
          obj.path.push(key);
          if (safe) {
            obj.path.pop();
          }
          return safe;
        });
      if (safe) {
        obj.path.pop();
      }
    }
    if (_.isElement(value)) {
      safe = false;
    }
    if (_.isFunction(value)) {
      let isModule: boolean = !!Object.getPrototypeOf(value)
        .toString()
        .match(/^function (PureComponent|Component)/);

      if (!isModule) {
        isModule = !!Object.getPrototypeOf(value)
          .toString()
          .match(/ModuleComponent/g);
      }

      if (isModule) {
        safe = false;
        obj.message = `Dispatcher Module Component "${value.name}" can't be passed as dispatcher data.`;
      } else if (['Component', 'PureComponent'].indexOf(value.name) !== -1) {
        safe = false;
        obj.message = `React Component "${value.name}" can't be passed as dispatcher data.`;
      }
    }
    return safe;
  };
  /**
   * Logs warning for potential misuse of dispatcher keywords.
   * @param prop
   */
  public validateForbiddenWords = (prop: string) => {
    if (DispatcherManager.OPERATION_PROPERTIES_TO_EVALUATE.includes(prop)) {
      error(`Potential misuse of "${prop}" data. Please rename that variable in order to ensure dispatcher consistent behaviour!`, 'warn');
    }
  };
  /**
   * If `ENABLE_DATA_PROXY` is enabled, then data will be wrapped with proxy, otherwise will return passed data.
   * @param data
   * @param {DispatcherManager} dispatcher
   */
  public getDataProxy = (data: object, dispatcher: DispatcherManager): any => {
    if (!DataProxy.dispatcher) {
      DataProxy.dispatcher = dispatcher;
    }
    if (this.ENABLE_DATA_PROXY) {
      return new Proxy(data, this.validator);
    }
    return new Proxy(data, this.updateStateVariables);
  };
}

const DataProxyInstance = new DataProxy();
export default DataProxyInstance;
